import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import { setHoveredItem, setClickedItem } from '../mosquito-observations.slice'

import './mosquito-observations-sightings-map-logic.styl'

function getSightingId(sighting) {
  return sighting?.id
}

function getSightingSource(sighting) {
  return sighting?.properties?.source || ''
}

function getSightingHoverKey(sighting) {
  return `${getSightingSource(sighting)}-${getSightingId(sighting) || ''}`
}

function clearSource(map, sourceId) {
  const source = map?.getSource(sourceId)
  if (!source) return

  source.setData({
    type: 'FeatureCollection',
    features: [],
  })
}

function MosquitoObservationsSightingsMapData({ map, sightings }) {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { hoveredItem } = useSelector(state => state.mosquitoDetection)

  useEffect(() => {
    if (!map) return

    const source = map.getSource('mosquito-observations')
    if (!source) return

    source.setData({
      type: 'FeatureCollection',
      features: sightings.map((sighting) => {
        const { geometry, properties } = sighting
        const hoverKey = getSightingHoverKey(sighting)
        return {
          type: 'Feature',
          geometry,
          properties: {
            ...properties,
            hoverKey,
            color: properties?.identification?.color || '#9ca3af',
          },
        }
      }),
    })

    return () => {
      const currentSource = map.getSource('mosquito-observations')
      if (currentSource) {
        currentSource.setData({
          type: 'FeatureCollection',
          features: [],
        })
      }
    }
  }, [map, sightings])

  useEffect(() => {
    if (!map) return

    const hoveredSource = map.getSource('hovered-mosquito-observations')
    if (!hoveredSource) return

    const isMosquitoObservation = Boolean(hoveredItem?.properties?.identification)

    if (!hoveredItem || !isMosquitoObservation) {
      clearSource(map, 'hovered-mosquito-observations')
      return
    }

    const { geometry, properties } = hoveredItem
    hoveredSource.setData({
      type: 'FeatureCollection',
      features: [{
        type: 'Feature',
        geometry,
        properties: {
          ...properties,
          color: properties?.identification?.color || '#9ca3af',
        },
      }],
    })

    return () => clearSource(map, 'hovered-mosquito-observations')
  }, [hoveredItem, map])

  useEffect(() => {
    if (!map) return
    const canvasContainer = document.querySelector('.maplibregl-canvas')

    const layerId = 'mosquito-observations-layer'

    const handleMouseMove = (event) => {
      const hoveredFeature = event?.features?.[0]
      if (!hoveredFeature) return

      const hoverKey = hoveredFeature?.properties?.hoverKey
      const hoveredSighting = sightings.find(
        sighting => getSightingHoverKey(sighting) === hoverKey,
      )
      if (!hoveredSighting) return

      canvasContainer.classList.add('pointer-cursor')
      dispatch(setHoveredItem(hoveredSighting))
    }

    const handleMouseLeave = () => {
      canvasContainer.classList.remove('pointer-cursor')
      dispatch(setHoveredItem(null))
      clearSource(map, 'hovered-mosquito-observations')
    }

    const handleClick = (event) => {
      navigate(`/mosquito-observations/sightings`)
      const clickedFeature = event?.features?.[0]
      if (!clickedFeature) return

      const hoverKey = clickedFeature?.properties?.hoverKey
      const clickedSighting = sightings.find(
        sighting => getSightingHoverKey(sighting) === hoverKey,
      )
      if (!clickedSighting) return

      dispatch(setClickedItem(clickedSighting))
    }

    let handlersAttached = false
    const attachHandlers = () => {
      if (handlersAttached || !map.getLayer(layerId)) return
      map.on('mousemove', layerId, handleMouseMove)
      map.on('mouseleave', layerId, handleMouseLeave)
      map.on('click', layerId, handleClick)
      handlersAttached = true
    }

    attachHandlers()
    map.on('idle', attachHandlers)
    map.on('styledata', attachHandlers)

    return () => {
      map.off('idle', attachHandlers)
      map.off('styledata', attachHandlers)
      if (handlersAttached) {
        map.off('mousemove', layerId, handleMouseMove)
        map.off('mouseleave', layerId, handleMouseLeave)
        map.off('click', layerId, handleClick)
      }
      map.getCanvas().style.cursor = ''
      dispatch(setHoveredItem(null))
      clearSource(map, 'hovered-mosquito-observations')
    }
  }, [dispatch, map, navigate, sightings])

  return null
}

export default MosquitoObservationsSightingsMapData
